import { useState, useRef, useEffect, type MouseEvent } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Search,
  Bell,
  Users,
  Plus,
  MessageCircle,
  Image as ImageIcon,
  Heart,
  Share2,
  MoreHorizontal,
  ChevronLeft,
  Send,
  Settings,
  Pin,
  User,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useGroups, useCreateGroup, useGroup, useGroupPosts, useJoinGroup } from '@/hooks/useGroups';
import { useLikePost, useCreatePost } from '@/hooks/usePost';
import { formatCount, timeAgo } from '@/lib/formatters';
import { processAvatarUrl } from '@/lib/mediaUtils';
import CommentModal from '@/components/CommentModal';
import type { Group, GroupPost } from '@/types';
import CreateGroupModal from '@/components/CreateGroupModal';

gsap.registerPlugin(ScrollTrigger);

/** Extract author display info from a GroupPost. */
function getGroupPostUser(post: GroupPost): { name: string; avatar: string } {
  const username = post.user?.username ?? post.username ?? 'Unknown';
  const rawAvatar = post.user?.avatar ?? '';
  return { name: username, avatar: processAvatarUrl(rawAvatar) || '' };
}

/** Get total likes count from a GroupPost's engagement. */
function getGroupPostLikes(post: GroupPost): number {
  const counts = post.postEngagement?.reactionCounts;
  if (!counts) return 0;
  return Object.values(counts).reduce<number>((sum, n) => sum + ((n as number) || 0), 0);
}

/** Get group identifier — prefer ulid, fall back to id. */
function getGroupId(group: Group): string {
  return (group.ulid ?? group.id) as string;
}

/** Get group image — prefer avatar, fall back to background. */
function getGroupImage(group: Group): string {
  const raw = group.avatar ?? group.background ?? '';
  return processAvatarUrl(raw) || '';
}

/** Get group description — prefer description, fall back to bio. */
function getGroupDescription(group: Group): string {
  return group.description ?? group.bio ?? '';
}

// ---------------------------------------------------------------------------
// Sub-component: Group Detail View
// ---------------------------------------------------------------------------

interface GroupDetailProps {
  groupUlid: string;
  onBack: () => void;
}

function GroupDetail({ groupUlid, onBack }: GroupDetailProps) {
  const { user: currentUser } = useAuth();
  const [activeCommentPostUlid, setActiveCommentPostUlid] = useState<string | null>(null);
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [newPostContent, setNewPostContent] = useState('');

  const { data: group, isLoading: isGroupLoading } = useGroup(groupUlid);
  const {
    data: postsData,
    isLoading: isPostsLoading,
  } = useGroupPosts(groupUlid);

  const likePost = useLikePost();
  const createPost = useCreatePost();
  const groupPosts = postsData?.pages.flatMap((p) => p.data) ?? [];

  const handleLike = (postUlid: string) => {
    const liked = likedPosts.has(postUlid);
    setLikedPosts((prev) => {
      const next = new Set(prev);
      if (liked) {
        next.delete(postUlid);
      } else {
        next.add(postUlid);
      }
      return next;
    });
    likePost.mutate({ ulid: postUlid, liked });
  };

  const openComments = (postUlid: string) => {
    setActiveCommentPostUlid(postUlid);
  };

  const closeComments = () => {
    setActiveCommentPostUlid(null);
  };

  const submitPost = () => {
    if (!newPostContent.trim() || !group) return;
    createPost.mutate(
      { body: newPostContent, groupUlid },
      { onSuccess: () => setNewPostContent('') },
    );
  };

  const rawUserAvatar = currentUser?.avatarUrl ?? currentUser?.avatar ?? '';
  const userAvatar = processAvatarUrl(rawUserAvatar) || '';

  if (isGroupLoading) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
        <Loader2 size={32} className="text-[#A38BFF] animate-spin" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
        <p className="text-[#A7B0C8]">Group not found.</p>
      </div>
    );
  }

  const groupImage = getGroupImage(group);
  const groupDescription = getGroupDescription(group);
  const isPrivate = group.visibility === 'PRIVATE';

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-20">
      {/* Group Header */}
      <div className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] pt-safe">
        <div className="flex items-center gap-3 px-4 py-3 pt-3">
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]"
          >
            <ChevronLeft size={22} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-[#F5F7FF] truncate">{group.name}</h1>
          </div>
          <button className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
            <Bell size={20} />
          </button>
          <button className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
            <Settings size={20} />
          </button>
        </div>
      </div>

      {/* Group Info */}
      <div className="relative">
        <div className="h-32 overflow-hidden">
          {groupImage ? (
            <img
              src={groupImage}
              alt={group.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[#141821] flex items-center justify-center">
              <Users size={48} className="text-[#2A2F3A]" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D10] to-transparent" />
        </div>
        <div className="px-4 -mt-8 relative z-10">
          <div className="flex items-end gap-4">
            <div className="w-20 h-20 rounded-2xl border-4 border-[#0B0D10] overflow-hidden bg-[#141821] flex items-center justify-center">
              {groupImage ? (
                <img
                  src={groupImage}
                  alt={group.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Users size={32} className="text-[#2A2F3A]" />
              )}
            </div>
          </div>
          <div className="mt-3">
            <h2 className="text-xl font-bold text-[#F5F7FF]">{group.name}</h2>
            {groupDescription ? (
              <p className="text-sm text-[#A7B0C8] mt-1">{groupDescription}</p>
            ) : null}
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5 text-sm text-[#A7B0C8]">
                <Users size={16} />
                <span>{formatCount(group.membersCount ?? 0)} members</span>
              </div>
              {isPrivate && (
                <span className="text-xs px-2 py-0.5 bg-[#141821] rounded-full text-[#A7B0C8]">
                  Private
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Post */}
      {group.isJoined && (
        <div className="px-4 mt-4">
          <div className="bg-[#141821] rounded-xl p-3">
            <div className="flex items-center gap-3">
              {userAvatar ? (
                <img
                  src={userAvatar}
                  alt="You"
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#2A2F3A] flex items-center justify-center flex-shrink-0">
                  <User size={18} className="text-[#A7B0C8]" />
                </div>
              )}
              <input
                type="text"
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Write something to the group..."
                className="flex-1 bg-transparent text-sm text-[#F5F7FF] placeholder-[#A7B0C8] outline-none"
                onKeyDown={(e) => e.key === 'Enter' && submitPost()}
              />
              <button
                onClick={submitPost}
                disabled={!newPostContent.trim() || createPost.isPending}
                className={`transition-colors ${newPostContent.trim() ? 'text-[#A38BFF]' : 'text-[#A7B0C8]'}`}
              >
                {createPost.isPending ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Send size={20} />
                )}
              </button>
              <button className="text-[#A38BFF]">
                <ImageIcon size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Posts */}
      <div className="px-4 py-4 space-y-4">
        {isPostsLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={32} className="text-[#A38BFF] animate-spin" />
          </div>
        ) : groupPosts.length === 0 ? (
          <div className="text-center py-12">
            <Users size={48} className="text-[#2A2F3A] mx-auto mb-3" />
            <p className="text-[#A7B0C8]">No posts yet</p>
            <p className="text-sm text-[#A7B0C8]">Be the first to post!</p>
          </div>
        ) : (
          groupPosts.map((post) => {
            const postUser = getGroupPostUser(post);
            const likesCount = getGroupPostLikes(post);
            const isLiked = likedPosts.has(post.ulid);
            const commentCount = post.postEngagement?.commentCount ?? 0;

            return (
              <div key={post.ulid} className="bg-[#141821] rounded-xl p-4">
                {post.isPinned && (
                  <div className="flex items-center gap-1 text-xs text-[#A38BFF] mb-2">
                    <Pin size={12} />
                    <span>Pinned</span>
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  {postUser.avatar ? (
                    <img
                      src={postUser.avatar}
                      alt={postUser.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#2A2F3A] flex items-center justify-center flex-shrink-0">
                      <User size={18} className="text-[#A7B0C8]" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-[#F5F7FF]">{postUser.name}</p>
                    <p className="text-xs text-[#A7B0C8]">
                      {post.createdAt ? timeAgo(post.createdAt) : ''}
                    </p>
                  </div>
                  <button className="text-[#A7B0C8]">
                    <MoreHorizontal size={20} />
                  </button>
                </div>

                {post.body ? (
                  <p className="text-sm text-[#F5F7FF] mb-3">{post.body}</p>
                ) : null}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleLike(post.ulid)}
                      className={`flex items-center gap-1.5 transition-colors ${
                        isLiked ? 'text-[#A38BFF]' : 'text-[#A7B0C8]'
                      }`}
                    >
                      <Heart size={18} fill={isLiked ? '#A38BFF' : 'none'} />
                      <span className="text-sm">{formatCount(likesCount)}</span>
                    </button>
                    <button
                      onClick={() => openComments(post.ulid)}
                      className="flex items-center gap-1.5 text-[#A7B0C8]"
                    >
                      <MessageCircle size={18} />
                      <span className="text-sm">{formatCount(commentCount)}</span>
                    </button>
                    <button className="flex items-center gap-1.5 text-[#A7B0C8]">
                      <Share2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Comments Modal */}
      {activeCommentPostUlid !== null && (
        <CommentModal
          postUlid={activeCommentPostUlid}
          onClose={closeComments}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Groups component — list view
// ---------------------------------------------------------------------------

export default function Groups() {
  const [selectedGroupUlid, setSelectedGroupUlid] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const {
    data: groupsData,
    isLoading: isGroupsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGroups();

  const joinGroup = useJoinGroup();

  const allGroups = groupsData?.pages.flatMap((p) => p.data) ?? [];

  const filteredGroups = searchQuery
    ? allGroups.filter(
        (g) =>
          g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          getGroupDescription(g).toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : allGroups;

  const myGroups = allGroups.filter((g) => g.isJoined === true);
  const discoverGroups = allGroups.filter((g) => !g.isJoined);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' },
      );

      if (contentRef.current) {
        gsap.fromTo(
          contentRef.current.children,
          { y: 24, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
            stagger: 0.08,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: contentRef.current,
              start: 'top 80%',
            },
          },
        );
      }
    });

    return () => ctx.revert();
  }, []);

  const handleJoinGroup = (group: Group, e: MouseEvent) => {
    e.stopPropagation();
    const id = getGroupId(group);
    if (!id) return;
    joinGroup.mutate({ groupId: id, isJoined: group.isJoined === true });
  };

  // If a group is selected, show its detail view
  if (selectedGroupUlid) {
    return (
      <GroupDetail
        groupUlid={selectedGroupUlid}
        onBack={() => setSelectedGroupUlid(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-20">
      {/* Top Bar */}
      <div
        ref={headerRef}
        className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 pb-3 pt-safe"
      >
        <div className="flex items-center justify-between pt-3">
          <h1 className="text-xl font-bold text-[#F5F7FF]" style={{ fontFamily: 'Space Grotesk' }}>
            Groups
          </h1>
          <div className="flex items-center gap-3">
            <button className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A38BFF]"
              onClick={() => setShowCreateModal(true)}>
              <Plus size={22} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-3">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search groups..."
              className="w-full bg-[#141821] rounded-xl pl-10 pr-4 py-2.5 text-sm text-[#F5F7FF] placeholder-[#A7B0C8] outline-none border border-white/[0.08]"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {isGroupsLoading ? (
        <div className="flex justify-center items-center py-24">
          <Loader2 size={32} className="text-[#A38BFF] animate-spin" />
        </div>
      ) : (
        <div ref={contentRef} className="px-4 py-4">
          {/* My Groups */}
          {!searchQuery && myGroups.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-[#F5F7FF]">My Groups</h2>
                <button className="text-sm text-[#A38BFF]">See All</button>
              </div>
              <div className="space-y-3">
                {myGroups.map((group) => {
                  const groupId = getGroupId(group);
                  const groupImage = getGroupImage(group);
                  return (
                    <div
                      key={groupId}
                      onClick={() => groupId && setSelectedGroupUlid(groupId)}
                      className="bg-[#141821] rounded-xl p-3 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
                    >
                      {groupImage ? (
                        <img
                          src={groupImage}
                          alt={group.name}
                          className="w-14 h-14 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-[#2A2F3A] flex items-center justify-center flex-shrink-0">
                          <Users size={24} className="text-[#A7B0C8]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#F5F7FF] truncate">{group.name}</h3>
                        <p className="text-xs text-[#A7B0C8]">
                          {formatCount(group.membersCount ?? 0)} members
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleJoinGroup(group, e)}
                        disabled={joinGroup.isPending}
                        className="px-3 py-1.5 bg-white/10 rounded-full text-xs text-white"
                      >
                        Joined
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Discover / Search Results */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-[#F5F7FF]">
                {searchQuery ? 'Search Results' : 'Discover'}
              </h2>
            </div>

            {searchQuery && filteredGroups.length === 0 ? (
              <div className="text-center py-12">
                <Search size={48} className="text-[#2A2F3A] mx-auto mb-3" />
                <p className="text-[#A7B0C8]">No groups found</p>
                <p className="text-sm text-[#A7B0C8]">Try a different search term</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(searchQuery ? filteredGroups : discoverGroups).map((group) => {
                  const groupId = getGroupId(group);
                  const groupImage = getGroupImage(group);
                  return (
                    <div
                      key={groupId}
                      onClick={() => groupId && setSelectedGroupUlid(groupId)}
                      className="bg-[#141821] rounded-xl p-3 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
                    >
                      {groupImage ? (
                        <img
                          src={groupImage}
                          alt={group.name}
                          className="w-14 h-14 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-[#2A2F3A] flex items-center justify-center flex-shrink-0">
                          <Users size={24} className="text-[#A7B0C8]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#F5F7FF] truncate">{group.name}</h3>
                        <p className="text-xs text-[#A7B0C8]">
                          {formatCount(group.membersCount ?? 0)} members
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleJoinGroup(group, e)}
                        disabled={joinGroup.isPending}
                        className="px-3 py-1.5 bg-[#A38BFF] rounded-full text-xs font-medium text-[#0B0D10] disabled:opacity-60"
                      >
                        {group.isJoined ? 'Joined' : 'Join'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Load more */}
            {hasNextPage && !searchQuery && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-6 py-2 bg-[#141821] rounded-full text-sm text-[#A38BFF] flex items-center gap-2 disabled:opacity-60"
                >
                  {isFetchingNextPage ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : null}
                  Load more
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      { /* Group Creation Modal */ }
      {showCreateModal && <CreateGroupModal onClose={() => setShowCreateModal(false)} />}
    </div>
  );
}
